import { isGeneratedName, suggestedName, type DeviceHints } from '../shared/naming';

export interface Identity {
  id: string;
  name: string;
  /** True once the person picked a name, so the first-run prompt only shows once. */
  named: boolean;
}

const STORAGE_KEY = 'chatsi.identity.v1';

/** Unmasked WebGL renderer; the only chip name a browser exposes on Apple Silicon. */
function gpuLabel(): string {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') as WebGLRenderingContext | null;
    if (!gl) return '';
    const info = gl.getExtension('WEBGL_debug_renderer_info');
    const renderer = info ? String(gl.getParameter(info.UNMASKED_RENDERER_WEBGL) ?? '') : '';
    gl.getExtension('WEBGL_lose_context')?.loseContext();
    return renderer;
  } catch {
    return '';
  }
}

/** Device details available synchronously in a browser, empty on the server. */
export function deviceHints(): DeviceHints {
  const nav = (globalThis as { navigator?: Navigator }).navigator;
  const view = globalThis.screen;
  const userAgent = nav?.userAgent;
  // Only Macs hide their chip name, and only behind WebGL, so avoid the hit elsewhere.
  const gpu = /Macintosh|Mac OS X/i.test(userAgent ?? '') ? gpuLabel() : '';
  return {
    userAgent,
    screen: view ? [view.width, view.height] : undefined,
    gpu,
    pixelRatio: globalThis.devicePixelRatio,
  };
}

function newId(): string {
  const cryptoApi = globalThis.crypto;
  if (cryptoApi?.randomUUID) return cryptoApi.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function saveIdentity(identity: Identity) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(identity));
  } catch {
    // Private mode or storage disabled: the identity lasts for this page only.
  }
}

/**
 * Stable per-browser identity so each client sees its own messages on the right.
 * Unnamed people get a suggestion built from their device, e.g. "iPhone 15 Pro #4271".
 */
export function loadIdentity(hints: DeviceHints = deviceHints()): Identity {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<Identity>;
      if (parsed.id) {
        const id = String(parsed.id);
        const storedName = typeof parsed.name === 'string' ? parsed.name.trim() : '';
        const named = typeof parsed.named === 'boolean' ? parsed.named : Boolean(storedName);
        // Nameless browsers (including older Chatsi# handles) pick up the device-based
        // suggestion; a name someone actually chose is never replaced.
        const name = named || (storedName && !isGeneratedName(storedName)) ? storedName : suggestedName(id, hints);
        const identity: Identity = { id, name, named };
        saveIdentity(identity);
        return identity;
      }
    }
  } catch {
    // fall through and create a fresh identity
  }
  const id = newId();
  const identity: Identity = { id, name: suggestedName(id, hints), named: false };
  saveIdentity(identity);
  return identity;
}
